import { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Close } from '@codex/organisms';
import PropTypes from 'prop-types';
import { Tooltip } from '@codex/basics';
import { Icon } from '@codex/icons';
import Configuration from '../../../../configuration';


const Title = styled.h2`
  color: ${({ theme }) => theme.colors.shades[0]};
  margin: 0;
  font-size: 23px;
`;

const TitleCont = styled.div`
  color: ${({ theme }) => theme.colors.shades[0]};
  text-align: center;
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  z-index: 4;
  margin: 20px 5% 0;
  background: ${({ theme }) => theme.colors.shades[7]};
`;

const slideInOrOut = (isSlideIn) => keyframes`
  from {
    -webkit-transform: translate3d(0, ${isSlideIn ? '100%' : 0}, 0);
    transform: translate3d(0, ${isSlideIn ? '100%' : 0}, 0);
    visibility: visible;
  }

  to {
    -webkit-transform: translate3d(0, ${isSlideIn ? 0 : '100%'}, 0);
    transform: translate3d(0, ${isSlideIn ? 0 : '100%'}, 0);
    visibility: hidden;
  }
`;

const Container = styled.div`
  height: 95%;
  overflow: hidden;
  overflow-y: auto;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: ${({ theme }) => theme.colors.shades[7]};
  box-shadow: 0 -3px 9px 0 rgba(0,0,0,0.08);
  animation: ${(props) => slideInOrOut(props.isSlideIn)} ease ${(props) => props.duration}s;
`;

const ContentCont = styled.div`
  position: absolute;
  top: 65px;
  width: 100%;
  background: ${({ theme }) => theme.colors.shades[7]};
`;

const CopyLink = styled.div`
  color: ${({ theme }) => theme.colors.link.default};
  position: absolute;
  left: 0;
  top: 50%;
  transform: translate(0, -50%);
  display: inline-block;
  cursor: pointer;
  border-radius: 50%;
  text-decoration: underline;
`;

const CloseCont = styled.div`
  background: ${({ theme }) => theme.colors.link.default};
  position: absolute;
  right: 0;
  top: 50%;
  transform: translate(0, -50%);
  display: inline-block;

  color: ${({ theme }) => theme.colors.shades[7]};
  cursor: pointer;
  border-radius: 50%;
`;

const Fade = styled.div`
  overflow: visible;
  z-index: 9999;
  position: fixed;
  padding: 0;
  margin: 0;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(34, 34, 34, 0.6);
`;

const InputTextCopyCont = styled.input`
  position: absolute;
  left: -9999px;
  top: -9999px;
`;

const TooltipCon = styled.div`
  width: 205px;
  position: absolute;
  top: 78%;
  left: -60px;
  padding-top: 10px;
  z-index: 4;
`;

const GreenTickIcon = styled(Icon)`
  color: green;
`;

const CopyIcon = styled(Icon)`
  margin-left: 10px;
`;

const BottomPopup = ({
  visible, children, title, duration, productIds, beforeClose
}) => {

  const [isTooltipOn, setIsTooltipOn] = useState(false);
  const [isSlideIn, setIsSlideIn] = useState(true);
  const copyText = useRef(null);

  useEffect(() => {
    if (visible) {
      document.body.style.overflowY = 'hidden';
    } else {
      document.body.style.overflowY = 'auto';
    }
  }, [visible]);

  const animateAndHide = () => {
    setIsSlideIn(false);

    setTimeout(() => {
      beforeClose();
      setIsSlideIn(true);
      document.body.style.overflowY = 'auto';
    }, duration * 1000);
  };

  const handleCopyLink = () => {
    setIsTooltipOn(true);

    if (!isTooltipOn) {
      setTimeout(() => {
        setIsTooltipOn(false);
      }, 3000);
    }

    // build copy link URL
    const copyLink = `${Configuration.get('listingPage.comparePagePrefixUrl')}${productIds.join('-')}.html`;

    // copy to the clipboard
    copyText.current.value = copyLink;
    copyText.current.select();
    copyText.current.setSelectionRange(0, 99999); /* For mobile device */
    document.execCommand('copy');
  };

  const renderTooltip = () => (
    isTooltipOn && (
    <TooltipCon>
      <Tooltip fadeInOut small type={'positive'} arrow={'top'} arrowMobile={'bottom'} textAlign={'center'}>
        <GreenTickIcon icon={'Tick'} />
        {' '}
        {/* TODO: Add to translations */}
        Item copied to clipboard!
      </Tooltip>
    </TooltipCon>
    )
  );

  return (
    <div>
      {
        visible && (
        <Fade
          id={'fade'}
          onClick={(e) => {
            if (e.target.id === 'fade' && productIds.length > 0) {
              // only allow hiding when there are products in popup
              animateAndHide();
            }
          }}
        >
          <Container isSlideIn={isSlideIn} duration={duration}>
            <TitleCont id={'title-con'}>
              <InputTextCopyCont ref={copyText} onClick={handleCopyLink} />
              {productIds.length > 1 && (
                <CopyLink onClick={handleCopyLink}>
                  {/* TODO: add to translations */}
                  {'Copy Link'}
                  <CopyIcon icon={'Copy'} size={'large'} />
                  {renderTooltip()}
                </CopyLink>
              )}
              <Title>{title}</Title>
              <CloseCont
                id={'close'}
                className={'close-cont'}
                onClick={() => {
                  if (productIds.length > 0) {
                    animateAndHide();
                  }
                }}
              >
                <Close />
              </CloseCont>
            </TitleCont>
            <ContentCont>
              {children}
            </ContentCont>
          </Container>
        </Fade>
        )
      }
    </div>
  );
};

BottomPopup.propTypes = {
  visible: PropTypes.bool.isRequired,
  beforeClose: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  productIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  duration: PropTypes.number
};

BottomPopup.defaultProps = {
  duration: 0.5
};

export default BottomPopup;
